<template>
    <div class="ah-page labor-tax-and-health-care">
        <div class="page-header bottom-margin">
            <div class="ah-container">
                <div class="title-row small">
                    <div class="title">
                        <h1>勞務稅款、二代健保申報</h1>
                    </div>
                    <div class="actions">
                        <el-button type="normal" @click="onExportBtnClick"> 匯出 </el-button>
                        <el-button type="normal" @click="onGoToLink('https://www.etax.nat.gov.tw/etwmain/etw144w')">
                            前往勞務申報繳稅服務
                            <i class="el-icon-link"></i>
                        </el-button>
                        <el-button type="normal" @click="onGoToLink('https://eservice.nhi.gov.tw/2nd/')">
                            前往二代健保申報及繳納作業
                            <i class="el-icon-link"></i
                        ></el-button>
                    </div>
                </div>
            </div>
        </div>
        <div class="ah-container">
            <div class="ah-panel">
                <div class="panel-body">
                    <el-form label-position="top" class="search-form">
                        <ahSelectTimeRange v-model="timespan" default="month" label="實際付款日區間" @change="fetchData" />
                        <el-form-item label="關鍵字">
                            <el-input v-model="keyword" style="width: 250px" placeholder="關鍵字：姓名、身分證號碼" @keyup.enter.native="fetchData">
                                <i slot="suffix" class="el-input__icon el-icon-search" style="cursor: pointer" @click="fetchData" />
                            </el-input>
                        </el-form-item>
                    </el-form>
                </div>
                <div class="summary-block">
                    <div class="summary-list">
                        <div class="item">
                            <div class="label">勞務總額</div>
                            <div class="value"><Money :v="totals.totalAmount" /></div>
                        </div>
                        <div class="item">
                            <div class="label">代扣勞務稅總額</div>
                            <div class="value"><Money :v="totals.totalTax" /></div>
                        </div>
                        <div class="item">
                            <div class="label">代扣二代健保總額</div>
                            <div class="value"><Money :v="totals.totalGen2" /></div>
                        </div>
                        <div class="item">
                            <div class="label">實付總額</div>
                            <div class="value"><Money :v="totals.totalActualAmount" /></div>
                        </div>
                    </div>
                </div>
                <div class="hint gray small">
                    <div>下列資料為「已完成的請款單」付款對象為「委外勞務」的資料列表。</div>
                </div>
                <div class="panel-body">
                    <el-table
                        :data="datas"
                        ref="table"
                        empty-text="無資料"
                        style="width: 100%"
                        show-summary
                        v-loading="isLoading"
                        :default-sort="{
                            prop: 'taX_PAY_DAT',
                            order: 'ascending'
                        }"
                        :summary-method="getTableSummaries"
                        @selection-change="onSelectionChange"
                    >
                        <el-table-column type="selection" width="58"></el-table-column>
                        <el-table-column prop="paY_DAT" label="實際付款日" sortable width="150">
                            <template slot="header"> 實際付款日 <ah-tip>請款單實際付款日期</ah-tip> </template>
                            <template slot-scope="scope">
                                {{ scope.row.paY_DAT | dateFormater("YYYY-MM-dd") }}
                            </template>
                        </el-table-column>
                        <el-table-column prop="paY_NAM" label="所得人姓名" width="135" sortable>
                            <template slot-scope="scope">
                                <div style="font-weight: bold">
                                    {{ scope.row.paY_NAM }}
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column prop="f_BSN" label="身分證/統編" width="120" sortable></el-table-column>
                        <el-table-column prop="serviceType" label="所得類型" width="130" sortable>
                            <template slot-scope="scope">
                                {{ getServiceTypeTxt(scope.row.serviceType) }}
                            </template>
                        </el-table-column>
                        <el-table-column prop="amt" label="勞務金額" align="right" min-width="120" sortable>
                            <template slot-scope="scope">
                                <Money :v="scope.row.amt" />
                            </template>
                        </el-table-column>
                        <el-table-column prop="tax" label="代扣稅額" align="right" min-width="120" sortable>
                            <template slot-scope="scope">
                                <Money :v="scope.row.tax" />
                            </template>
                        </el-table-column>
                        <el-table-column prop="geN2_AMT" label="代扣二代健保" align="right" min-width="130" sortable>
                            <template slot-scope="scope">
                                <Money :v="scope.row.geN2_AMT" />
                            </template>
                        </el-table-column>
                        <el-table-column prop="act_amt" label="實付金額" align="right" min-width="120" sortable>
                            <template slot-scope="scope">
                                <Money :v="scope.row.amt - scope.row.tax - scope.row.geN2_AMT" />
                            </template>
                        </el-table-column>
                        <el-table-column prop="source" label="來源請款單" width="115" sortable>
                            <template slot-scope="scope">
                                <el-button type="text" @click="onOpenPaymentDetail(scope.row.sno)">
                                    {{ scope.row.payoo }}
                                </el-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </div>
        </div>
        <PaymentDetailDialog :visible="paymentDetailDialog.visible" :sno="paymentDetailDialog.sno" name="paymentDialog" @close="onPaymentDetailDialogClose" />
    </div>
</template>
<script>
import _ from 'lodash';
import moment from 'moment';
import { MyXLSX2 } from '@/utils/xlsxExport2';
import { serviceTypeList } from '@/assets/commonData/options';
import ahSelectTimeRange from '@/components/ui/ahSelectTimeRange.vue';

export default {
    components: {
        ahSelectTimeRange,
        PaymentDetailDialog: () => import('@/components/dashboard/Payment/PaymentDetailDialog.vue'),
    },
    data() {
        return {
            isLoading: false,
            selectedItems: [],
            timespan: [],
            keyword: null,
            queryParams: {
                QueryType: 2, // 1 發票收入 2 請款支出
                StartDate: null,
                EndDate: null,
                Keyword: undefined,
            },
            paymentDetailDialog: {
                visible: false,
                sno: null,
            },
            totals: {
                totalAmount: 0,
                totalTax: 0,
                totalGen2: 0,
                totalActualAmount: 0,
            },
            datas: [],
        };
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        async fetchData() {
            try {
                this.isLoading = true;
                const [startDate, endDate] = this.timespan;
                this.queryParams.StartDate = startDate;
                this.queryParams.EndDate = endDate;
                this.queryParams.Keyword = this.keyword || undefined;

                const resp = await this.$api({
                    url: 'AccountingSummaryPage/QueryLabor',
                    params: this.queryParams,
                    method: 'GET',
                });

                console.log('QueryLabor', resp.data);
                this.datas = resp.data.laborItems;

                this.totals.totalAmount = _.sumBy(this.datas, 'amt');
                this.totals.totalTax = _.sumBy(this.datas, 'tax');
                this.totals.totalGen2 = _.sumBy(this.datas, 'geN2_AMT');
                this.totals.totalActualAmount = this.totals.totalAmount - this.totals.totalTax - this.totals.totalGen2;
            } finally {
                this.isLoading = false;
            }
        },
        onSelectionChange(val) {
            this.selectedItems = val;
        },
        getTableSummaries(param) {
            const isAnyItemSelected = !!this.selectedItems.length;
            const { columns, data } = param;
            const sums = [];
            const totalAmount = _.sumBy(isAnyItemSelected ? this.selectedItems : data, 'amt');
            const totalTax = _.sumBy(isAnyItemSelected ? this.selectedItems : data, 'tax');
            const totalGen2 = _.sumBy(isAnyItemSelected ? this.selectedItems : data, 'geN2_AMT');
            const actualAmount = totalAmount - totalTax - totalGen2;

            columns.forEach((column, index) => {
                if (column.property === 'paY_DAT') {
                    sums[index] = `${isAnyItemSelected ? '(已勾選)' : ''} 總計`;
                    return;
                }

                if (column.property === 'amt') {
                    sums[index] = totalAmount !== 0 ? Number(totalAmount).toLocaleString('en') : '-';
                    return;
                }

                if (column.property === 'tax') {
                    sums[index] = totalTax !== 0 ? Number(totalTax).toLocaleString('en') : '-';

                    return;
                }

                if (column.property === 'geN2_AMT') {
                    sums[index] = totalGen2 !== 0 ? Number(totalGen2).toLocaleString('en') : '-';
                    return;
                }

                if (column.property === 'act_amt') {
                    sums[index] = actualAmount !== 0 ? Number(actualAmount).toLocaleString('en') : '-';
                }
            });

            return sums;
        },
        onPaymentDetailDialogClose() {
            this.paymentDetailDialog.visible = false;
            this.paymentDetailDialog.sno = null;
        },
        onOpenPaymentDetail(sno) {
            if (!sno) return;
            this.paymentDetailDialog.sno = sno;
            this.paymentDetailDialog.visible = true;
        },

        onExportBtnClick() {
            const wb = MyXLSX2.createBook();
            const title = '勞務稅、健保申報';

            // 內容表頭
            const bodyHeader = [
                { text: '', width: 5, align: 'center' },
                { text: '實際付款日', align: 'center' },
                { text: '所得人姓名', width: 25, align: 'left' },
                { text: '身分證/統編', align: 'left' },
                { text: '所得類型', align: 'left' },
                {
                    text: '勞務金額',
                    width: 13,
                    align: 'right',
                    number: true,
                },
                {
                    text: '代扣稅額',
                    width: 13,
                    align: 'right',
                    number: true,
                },
                {
                    text: '代扣二代健保',
                    width: 13,
                    align: 'right',
                    number: true,
                },
                {
                    text: '實付金額',
                    width: 13,
                    align: 'right',
                    number: true,
                },
                { text: '來源請款單', align: 'center' },
                { text: '請款單日期', align: 'center' },
            ];
            // 內容資料
            const bodyRows = [];
            const target = this.selectedItems.length ? this.selectedItems : this.datas;
            target.forEach((item, i) => {
                const row = [
                    i + 1,
                    moment(item.paY_DAT).format('YYYY-MM-DD') || '',
                    item.paY_NAM,
                    item.f_BSN,
                    this.getServiceTypeTxt(item.serviceType),
                    item.amt,
                    item.tax,
                    item.geN2_AMT,
                    item.amt - item.tax - item.geN2_AMT,
                    item.payoo,
                    moment(item.dat).format('YYYY-MM-DD') || '',
                ];
                bodyRows.push(row);
            });
            // 總計列
            const totalAmt = _.sumBy(target, 'amt');
            const totalTax = _.sumBy(target, 'tax');
            const totalGen2 = _.sumBy(target, 'geN2_AMT');
            bodyRows.push(['', '總計', '', '', '', totalAmt, totalTax, totalGen2, totalAmt - totalTax - totalGen2, '', '']);

            // 日期資訊
            const sd = moment(Number(this.queryParams.StartDate)).format('YYYY-MM-DD');
            const ed = moment(Number(this.queryParams.EndDate)).format('YYYY-MM-DD');
            const cd = moment().format('YYYY-MM-DD');
            const headRows = [
                { label: '查詢區間', value: `${sd} 至 ${ed}` },
                { label: '製表日期', value: cd },
            ];

            const sheet = MyXLSX2.createSheet(title, headRows, bodyHeader, bodyRows, true);
            MyXLSX2.bookAppendSheet(wb, sheet, '匯出');
            MyXLSX2.download(wb, `${this.getExportFileName()}.xlsx`);
        },
        getExportFileName() {
            const sd = moment(Number(this.queryParams.StartDate)).format('YYYY-MM-DD');
            const ed = moment(Number(this.queryParams.EndDate)).format('YYYY-MM-DD');
            return `勞務稅、健保申報(${sd}_${ed})`;
        },
        onGoToLink(link) {
            window.open(link);
        },
        getServiceTypeTxt(serviceTypeNum) {
            const s = serviceTypeList.find((x) => x.value === serviceTypeNum);
            if (s) return s.label;
            return '';
        },
    },
};
</script>

<style lang="scss" scoped="">
@import "@/assets/scss/vars.scss";
.labor-tax-and-health-care {
    .search-form {
        display: flex;
        padding: 0 10px;
        gap: 40px;
        margin-top: -8px;

        @media (max-width: 768px) {
            flex-direction: column;
            gap: 0px;
            margin-top: 0px;
        }
    }
    .hint {
        position: relative;
        background-color: rgb(70, 60, 178, 0.1);
        margin: 0 20px;
        padding: 10px 0;
        color: $main-color;
        &::before {
            content: "";
            position: absolute;
            background-color: #6f3dd9;
            height: 100%;
            width: 5px;
            top: 0;
            left: 0;
        }
        > * {
            margin-left: 20px;
        }
    }

    .summary-block {
        padding: 20px 50px;
        margin-bottom: 10px;
        border-top: 1px solid #eee;
        overflow: auto;

        .summary-list {
            display: flex;
            gap: 60px;
            min-width: 500px;

            >.item {
                >.label {
                    color: #606266;
                    white-space: nowrap;
                    font-size: 10.5pt;
                }
                >.value {

                }
            }
        }
    }
}
</style>

<style lang="scss">
.labor-tax-and-health-care .el-table__footer-wrapper tbody td {
    font-weight: bold;
}
</style>
